import { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import RichTextEditor, { markdownToHtml } from '../../components/RichTextEditor';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Mail,
  Plus,
  Edit2,
  Trash2,
  Send,
  Users,
  FileText,
  Loader2,
  Clock,
  Eye,
  X,
  Cake,
  Settings,
  Play,
  Gift,
  Tags,
} from 'lucide-react';
import { sanitizeHtml } from '../../utils/sanitize';
import { BillingPlaceholderPicker } from '../../components/admin/BillingPlaceholderPicker';
import { useFeatures } from '../../context/FeatureContext';

// CATEGORIES constant is now fetched dynamically from API

// A template is actionable only when the related product workflow is in the
// tenant's effective entitlement set. General operational email remains part
// of Transactional & Marketing Email; specialised templates do not leak into
// a lane-only tenant simply because that tenant can send email.
const TEMPLATE_CATEGORY_FEATURES = {
  billing: 'payments.module',
  birthday: 'comms.birthday',
  camp: 'camps.core',
  lane: 'bookings.lanes',
  movement: 'enrollments.core',
  payment: 'payments.module',
  small_group: 'bookings.small_groups',
  subscription: 'enrollments.monthly_billing',
  tournament: 'tournaments.registrations',
  trial: 'enrollments.core',
  uniform: 'commerce.uniforms',
};

const PLACEHOLDERS = [
  { key: '{{parent_name}}', description: 'Parent\'s name' },
  { key: '{{player_name}}', description: 'Player\'s name' },
  { key: '{{player_names}}', description: 'All player names (comma separated)' },
  { key: '{{email}}', description: 'Parent\'s email' },
  { key: '{{program_name}}', description: 'Program name' },
  { key: '{{amount}}', description: 'Payment amount' },
  { key: '{{date}}', description: 'Current date' },
];

const _BIRTHDAY_PLACEHOLDERS = [
  { key: '{{player_name}}', description: 'Birthday player\'s name' },
  { key: '{{recipient_name}}', description: 'Email recipient\'s name (player or parent)' },
  { key: '{{age}}', description: 'Player\'s new age' },
  { key: '{{academy_name}}', description: 'Academy name' },
  { key: '{{discount_code}}', description: 'Unique discount code for player' },
  { key: '{{discount_percentage}}', description: 'Discount percentage (20)' },
];

const EmailTemplates = () => {
  const { isPlanFeatureEnabled } = useFeatures();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('templates');
  
  // Categories state
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    value: '',
    color: 'bg-slate-100 text-slate-700',
    description: ''
  });
  const [savingCategory, setSavingCategory] = useState(false);
  
  // Template dialog state
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    subject: '',
    body: '',
    category: 'general',
    description: ''
  });
  
  // Bulk email dialog state
  const [showBulkEmailDialog, setShowBulkEmailDialog] = useState(false);
  const [bulkEmailForm, setBulkEmailForm] = useState({
    recipient_type: 'all',
    program_ids: [],
    selected_recipients: [], // For individual selection
    custom_subject: '',
    custom_body: ''
  });
  const [recipientPreview, setRecipientPreview] = useState({ total_count: 0, preview: [] });
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Individual recipient search
  const [recipientSearch, setRecipientSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchingRecipients, setSearchingRecipients] = useState(false);

  // Preview dialog
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  
  // Delete confirmation
  const [deleteTemplateDialog, setDeleteTemplateDialog] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  // Automated email settings
  const [automatedSettings, setAutomatedSettings] = useState({
    birthday_emails_enabled: true,
    welcome_emails_enabled: true,
    payment_reminder_enabled: true
  });
  const [birthdayTemplate, setBirthdayTemplate] = useState(null);
  const [_editingBirthdayTemplate, _setEditingBirthdayTemplate] = useState(false);
  const [_birthdayTemplateForm, setBirthdayTemplateForm] = useState({
    name: '',
    subject: '',
    body: '',
    is_active: true
  });
  const [birthdayLogs, setBirthdayLogs] = useState([]);
  const [sendingBirthdayEmails, setSendingBirthdayEmails] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [templatesRes, programsRes, logsRes, settingsRes, birthdayRes, birthdayLogsRes, categoriesRes] = await Promise.all([
        api.get('/email/templates'),
        api.get('/programs'),
        api.get('/email/logs'),
        api.get('/email/automated-settings').catch(() => ({ data: {} })),
        api.get('/email/birthday-template').catch(() => ({ data: null })),
        api.get('/email/birthday-log').catch(() => ({ data: [] })),
        api.get('/email/categories').catch(() => ({ data: [] }))
      ]);
      setTemplates(templatesRes.data.templates || []);
      setPrograms(programsRes.data.programs || []);
      setEmailLogs(logsRes.data.logs || []);
      setCategories(categoriesRes.data || []);
      setAutomatedSettings(settingsRes.data || {
        birthday_emails_enabled: true,
        welcome_emails_enabled: true,
        payment_reminder_enabled: true
      });
      if (birthdayRes.data) {
        setBirthdayTemplate(birthdayRes.data);
        setBirthdayTemplateForm({
          name: birthdayRes.data.name || '',
          subject: birthdayRes.data.subject || '',
          body: birthdayRes.data.body || '',
          is_active: birthdayRes.data.is_active !== false
        });
      }
      setBirthdayLogs(birthdayLogsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Category management
  const handleCreateCategory = async () => {
    if (!categoryForm.name || !categoryForm.value) {
      toast.error('Name and value are required');
      return;
    }
    
    setSavingCategory(true);
    try {
      await api.post('/email/categories', {
        name: categoryForm.name,
        value: categoryForm.value.toLowerCase().replace(/\s+/g, '_'),
        color: categoryForm.color,
        description: categoryForm.description
      });
      toast.success('Category created successfully');
      setShowCategoryDialog(false);
      setCategoryForm({ name: '', value: '', color: 'bg-slate-100 text-slate-700', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    } finally {
      setSavingCategory(false);
    }
  };

  const handleDeleteCategory = async (categoryValue) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    
    try {
      await api.delete(`/email/categories/${categoryValue}`);
      toast.success('Category deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const isTemplateCategoryAvailable = (category) => {
    const requiredFeature = TEMPLATE_CATEGORY_FEATURES[category];
    return !requiredFeature || isPlanFeatureEnabled(requiredFeature);
  };
  const visibleCategories = categories.filter((category) =>
    isTemplateCategoryAvailable(category.value)
  );
  const visibleTemplates = templates.filter((template) =>
    isTemplateCategoryAvailable(template.category)
  );

  // Filter only templates the tenant can actually use. The same predicate
  // drives category selectors and the bulk-send picker below.
  const filteredTemplates = visibleTemplates.filter((template) => {
    if (categoryFilter === 'all') return template.category !== 'birthday';
    return template.category === categoryFilter;
  });

  // Template CRUD
  const openCreateTemplateDialog = () => {
    setSelectedTemplate(null);
    setTemplateForm({
      name: '',
      subject: '',
      body: '',
      category: 'general',
      description: ''
    });
    setShowTemplateDialog(true);
  };

  const openEditTemplateDialog = (template) => {
    setSelectedTemplate(template);
    setTemplateForm({
      name: template.name,
      subject: template.subject,
      body: template.body,
      category: template.category,
      is_active: template.is_active !== false,
      description: template.description || ''
    });
    setShowTemplateDialog(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name || !templateForm.subject || !templateForm.body) {
      toast.error('Name, subject, and body are required');
      return;
    }

    setSaving(true);
    try {
      // Check if editing birthday template
      if (templateForm.category === 'birthday' || selectedTemplate?.category === 'birthday') {
        await api.put('/email/birthday-template', {
          name: templateForm.name,
          subject: templateForm.subject,
          body: templateForm.body,
          is_active: templateForm.is_active,
          description: templateForm.description
        });
        toast.success('Birthday template updated');
      } else if (selectedTemplate) {
        await api.put(`/email/templates/${selectedTemplate.id}`, templateForm);
        toast.success('Template updated');
      } else {
        await api.post('/email/templates', templateForm);
        toast.success('Template created');
      }
      setShowTemplateDialog(false);
      setSelectedTemplate(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteTemplateDialog = (templateId) => {
    setTemplateToDelete(templateId);
    setDeleteTemplateDialog(true);
  };

  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;
    
    try {
      await api.delete(`/email/templates/${templateToDelete}`);
      toast.success('Template deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    } finally {
      setDeleteTemplateDialog(false);
      setTemplateToDelete(null);
    }
  };

  // Bulk Email
  const openBulkEmailDialog = (template = null) => {
    setSelectedTemplate(template);
    setBulkEmailForm({
      recipient_type: 'all',
      program_ids: [],
      selected_recipients: [],
      enrollment_status_filter: 'active_or_paused', // Default to active/paused enrollments
      custom_subject: template?.subject || '',
      custom_body: template?.body || ''
    });
    setRecipientSearch('');
    setSearchResults([]);
    setRecipientPreview({ total_count: 0, preview: [] });
    setShowBulkEmailDialog(true);
    
    // Always fetch initial preview for "all" type
    fetchRecipientPreview('all', [], 'active_or_paused');
  };

  const fetchRecipientPreview = async (recipientType, programIds, enrollmentStatusFilter = 'active_or_paused') => {
    try {
      const res = await api.post('/email/preview-recipients', {
        recipient_type: recipientType,
        program_ids: programIds,
        enrollment_status_filter: enrollmentStatusFilter
      });
      setRecipientPreview(res.data);
    } catch (error) {
      console.error('Error fetching preview:', error);
    }
  };

  const handleRecipientTypeChange = (type) => {
    setBulkEmailForm({ ...bulkEmailForm, recipient_type: type, program_ids: [], selected_recipients: [] });
    setRecipientSearch('');
    setSearchResults([]);
    if (type === 'all' || type === 'by_program') {
      fetchRecipientPreview(type, [], bulkEmailForm.enrollment_status_filter);
    } else {
      setRecipientPreview({ total_count: 0, preview: [] });
    }
  };
  
  const handleEnrollmentStatusFilterChange = (filter) => {
    setBulkEmailForm({ ...bulkEmailForm, enrollment_status_filter: filter });
    // Refetch preview with new filter
    if (bulkEmailForm.recipient_type !== 'individual') {
      fetchRecipientPreview(bulkEmailForm.recipient_type, bulkEmailForm.program_ids, filter);
    }
  };

  // Search for individual recipients (parents and players with email)
  const searchRecipients = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearchingRecipients(true);
    try {
      const res = await api.get(`/email/search-recipients?q=${encodeURIComponent(query)}`);
      setSearchResults(res.data.results || []);
    } catch (error) {
      console.error('Error searching recipients:', error);
    } finally {
      setSearchingRecipients(false);
    }
  };

  const handleAddRecipient = (recipient) => {
    // Check if already added
    if (bulkEmailForm.selected_recipients.some(r => r.email === recipient.email)) {
      toast.info('Recipient already added');
      return;
    }
    
    const newRecipients = [...bulkEmailForm.selected_recipients, recipient];
    setBulkEmailForm({ ...bulkEmailForm, selected_recipients: newRecipients });
    setRecipientPreview({
      total_count: newRecipients.length,
      preview: newRecipients.slice(0, 10)
    });
    setRecipientSearch('');
    setSearchResults([]);
  };

  const handleRemoveRecipient = (email) => {
    const newRecipients = bulkEmailForm.selected_recipients.filter(r => r.email !== email);
    setBulkEmailForm({ ...bulkEmailForm, selected_recipients: newRecipients });
    setRecipientPreview({
      total_count: newRecipients.length,
      preview: newRecipients.slice(0, 10)
    });
  };

  const handleProgramSelect = (programId) => {
    const newProgramIds = bulkEmailForm.program_ids.includes(programId)
      ? bulkEmailForm.program_ids.filter(id => id !== programId)
      : [...bulkEmailForm.program_ids, programId];
    
    setBulkEmailForm({ ...bulkEmailForm, program_ids: newProgramIds });
    
    if (newProgramIds.length > 0) {
      fetchRecipientPreview('by_program', newProgramIds, bulkEmailForm.enrollment_status_filter);
    } else {
      setRecipientPreview({ total_count: 0, preview: [] });
    }
  };

  const handleSendBulkEmail = async () => {
    if (!selectedTemplate) {
      toast.error('Please select a template');
      return;
    }
    
    if (bulkEmailForm.recipient_type === 'individual' && bulkEmailForm.selected_recipients.length === 0) {
      toast.error('Please add at least one recipient');
      return;
    }
    
    if (bulkEmailForm.recipient_type !== 'individual' && recipientPreview.total_count === 0) {
      toast.error('No recipients selected');
      return;
    }

    setSending(true);
    try {
      const payload = {
        template_id: selectedTemplate.id,
        recipient_type: bulkEmailForm.recipient_type,
        program_ids: bulkEmailForm.program_ids,
        enrollment_status_filter: bulkEmailForm.enrollment_status_filter,
        selected_emails: bulkEmailForm.recipient_type === 'individual' 
          ? bulkEmailForm.selected_recipients.map(r => r.email)
          : [],
        custom_subject: bulkEmailForm.custom_subject !== selectedTemplate.subject ? bulkEmailForm.custom_subject : null,
        custom_body: bulkEmailForm.custom_body !== selectedTemplate.body ? bulkEmailForm.custom_body : null
      };
      
      const res = await api.post('/email/send-bulk', payload);
      
      toast.success(`Email sent to ${res.data.sent} recipients`);
      if (res.data.failed > 0) {
        toast.warning(`${res.data.failed} emails failed to send`);
      }
      
      setShowBulkEmailDialog(false);
      fetchData(); // Refresh logs
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSending(false);
    }
  };

  const getCategoryBadge = (category) => {
    const cat = categories.find(c => c.value === category) || { name: category, color: 'bg-slate-100 text-slate-700' };
    return <Badge className={cat.color}>{cat.name}</Badge>;
  };

  const insertPlaceholder = (placeholder) => {
    setTemplateForm(prev => ({
      ...prev,
      body: prev.body + placeholder
    }));
  };

  if (loading) {
    return (
      <DashboardLayout title="Email Templates">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Email Templates">
      <div className="space-y-6" data-testid="email-templates-page">
        <div className="space-y-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Email Management</h1>
            <p className="text-slate-600">Create templates and send bulk emails to parents</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => openBulkEmailDialog()}
              data-testid="send-bulk-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              Send Bulk Email
            </Button>
            <Button
              onClick={openCreateTemplateDialog}
              className="bg-blue-500 hover:bg-blue-600"
              data-testid="create-template-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Template
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="templates" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Templates ({visibleTemplates.length})
            </TabsTrigger>
            <TabsTrigger value="automated" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Automated Emails
            </TabsTrigger>
            <TabsTrigger value="logs" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Email Logs
            </TabsTrigger>
          </TabsList>

          {/* Templates Tab */}
          <TabsContent value="templates" className="mt-6">
            {/* Category Filter and Management */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Filter by Type:</Label>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-48" data-testid="category-filter">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {visibleCategories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        <div className="flex items-center gap-2">
                          <span className={`inline-block w-2 h-2 rounded-full ${cat.color.split(' ')[0]}`}></span>
                          {cat.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowCategoryDialog(true)}
                data-testid="manage-categories-btn"
              >
                <Tags className="h-4 w-4 mr-2" />
                Manage Types
              </Button>
              
              {categoryFilter !== 'all' && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setCategoryFilter('all')}
                >
                  Clear Filter
                </Button>
              )}
              
              <div className="ml-auto text-sm text-slate-500">
                Showing {filteredTemplates.length} of {visibleTemplates.filter(t => t.category !== 'birthday').length} templates
              </div>
            </div>

            {visibleTemplates.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <Mail className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500 mb-4">No email templates yet</p>
                  <Button onClick={openCreateTemplateDialog} variant="outline">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Your First Template
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Birthday Template - always show first when no filter or birthday filter */}
                {birthdayTemplate && isTemplateCategoryAvailable('birthday') && (categoryFilter === 'all' || categoryFilter === 'birthday') && (
                  <Card className="hover:shadow-md transition-shadow border-pink-200" data-testid="template-card-birthday">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-base flex items-center gap-2">
                            <Cake className="h-4 w-4 text-pink-500" />
                            {birthdayTemplate.name}
                          </CardTitle>
                          <div className="mt-1">
                            <Badge className="bg-pink-100 text-pink-700">Birthday</Badge>
                            {birthdayTemplate.is_active ? (
                              <Badge className="ml-1 bg-green-100 text-green-700">Active</Badge>
                            ) : (
                              <Badge className="ml-1" variant="outline">Inactive</Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setPreviewTemplate(birthdayTemplate);
                              setShowPreviewDialog(true);
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setTemplateForm({
                                name: birthdayTemplate.name || '',
                                subject: birthdayTemplate.subject || '',
                                body: birthdayTemplate.body || '',
                                category: 'birthday',
                                is_active: birthdayTemplate.is_active !== false,
                                description: birthdayTemplate.description || ''
                              });
                              setSelectedTemplate(birthdayTemplate);
                              setShowTemplateDialog(true);
                            }}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="text-sm text-slate-500 mb-2 line-clamp-2">
                        Subject: {birthdayTemplate.subject}
                      </p>
                      <p className="text-xs text-slate-400 mb-3">
                        <Gift className="h-3 w-3 inline mr-1" />
                        Automated daily at 8:00 AM with 20% discount
                      </p>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full border-pink-200 text-pink-700 hover:bg-pink-50"
                        onClick={async () => {
                          try {
                            setSendingBirthdayEmails(true);
                            const res = await api.post('/email/send-birthday-emails');
                            toast.success(`Birthday emails: ${res.data.sent} sent, ${res.data.skipped} skipped`);
                            fetchData();
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to send');
                          } finally {
                            setSendingBirthdayEmails(false);
                          }
                        }}
                        disabled={sendingBirthdayEmails || !automatedSettings.birthday_emails_enabled}
                      >
                        {sendingBirthdayEmails ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4 mr-2" />
                        )}
                        Send Today's Birthdays
                      </Button>
                    </CardContent>
                  </Card>
                )}
                {/* Regular Templates - use filtered list */}
                {filteredTemplates.map(template => (
                  <Card key={template.id} className="hover:shadow-md transition-shadow" data-testid={`template-card-${template.id}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-base">{template.name}</CardTitle>
                          <div className="mt-1">{getCategoryBadge(template.category)}</div>
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setPreviewTemplate(template);
                              setShowPreviewDialog(true);
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openEditTemplateDialog(template)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          {!template.is_system && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={() => openDeleteTemplateDialog(template.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="text-sm text-slate-500 mb-3 line-clamp-2">
                        Subject: {template.subject}
                      </p>
                      {template.description && (
                        <p className="text-xs text-slate-400 mb-3">{template.description}</p>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full"
                        onClick={() => openBulkEmailDialog(template)}
                      >
                        <Send className="h-4 w-4 mr-2" />
                        Use Template
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Automated Emails Tab */}
          <TabsContent value="automated" className="mt-6 space-y-6">
            {/* Settings Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Automated Email Settings
                </CardTitle>
                <CardDescription>
                  Enable or disable automated email notifications
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Cake className="h-5 w-5 text-pink-500" />
                    <div>
                      <p className="font-medium">Birthday Emails</p>
                      <p className="text-sm text-slate-500">Send birthday wishes with discount code to players</p>
                    </div>
                  </div>
                  <Switch
                    checked={automatedSettings.birthday_emails_enabled}
                    onCheckedChange={async (checked) => {
                      try {
                        setSavingSettings(true);
                        await api.put('/email/automated-settings', {
                          ...automatedSettings,
                          birthday_emails_enabled: checked
                        });
                        setAutomatedSettings(prev => ({ ...prev, birthday_emails_enabled: checked }));
                        toast.success(`Birthday emails ${checked ? 'enabled' : 'disabled'}`);
                      } catch (error) {
                        toast.error('Failed to update settings');
                      } finally {
                        setSavingSettings(false);
                      }
                    }}
                    disabled={savingSettings}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-green-500" />
                    <div>
                      <p className="font-medium">Welcome Emails</p>
                      <p className="text-sm text-slate-500">Send welcome emails to new parents and players</p>
                    </div>
                  </div>
                  <Switch
                    checked={automatedSettings.welcome_emails_enabled}
                    onCheckedChange={async (checked) => {
                      try {
                        setSavingSettings(true);
                        await api.put('/email/automated-settings', {
                          ...automatedSettings,
                          welcome_emails_enabled: checked
                        });
                        setAutomatedSettings(prev => ({ ...prev, welcome_emails_enabled: checked }));
                        toast.success(`Welcome emails ${checked ? 'enabled' : 'disabled'}`);
                      } catch (error) {
                        toast.error('Failed to update settings');
                      } finally {
                        setSavingSettings(false);
                      }
                    }}
                    disabled={savingSettings}
                  />
                </div>

                <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Clock className="h-5 w-5 text-blue-500" />
                      <div>
                        <p className="font-medium">Payment Reminders</p>
                        <p className="text-sm text-slate-500">
                          Auto-emails parents 7d / 3d / 1d before due, on the due date,
                          and every N days after — across all invoice types.
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={automatedSettings.payment_reminder_enabled}
                      onCheckedChange={async (checked) => {
                        try {
                          setSavingSettings(true);
                          await api.put('/email/automated-settings', {
                            ...automatedSettings,
                            payment_reminder_enabled: checked
                          });
                          setAutomatedSettings(prev => ({ ...prev, payment_reminder_enabled: checked }));
                          toast.success(`Payment reminders ${checked ? 'enabled' : 'disabled'}`);
                        } catch (error) {
                          toast.error('Failed to update settings');
                        } finally {
                          setSavingSettings(false);
                        }
                      }}
                      disabled={savingSettings}
                      data-testid="payment-reminder-toggle"
                    />
                  </div>

                  {/* Schedule controls — only shown when enabled to keep
                      the card compact when admins have it off. */}
                  {automatedSettings.payment_reminder_enabled && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-3 border-t">
                      <div>
                        <Label className="text-xs text-slate-600">Overdue interval (days)</Label>
                        <Input
                          type="number"
                          min={1}
                          max={30}
                          value={automatedSettings.payment_reminder_overdue_interval_days ?? 2}
                          onChange={(e) => {
                            const v = Math.max(1, Math.min(30, Number(e.target.value) || 1));
                            setAutomatedSettings(prev => ({ ...prev, payment_reminder_overdue_interval_days: v }));
                          }}
                          onBlur={async () => {
                            try {
                              setSavingSettings(true);
                              await api.put('/email/automated-settings', automatedSettings);
                              toast.success('Schedule updated');
                            } catch {
                              toast.error('Failed to save');
                            } finally {
                              setSavingSettings(false);
                            }
                          }}
                          className="h-9"
                          data-testid="payment-reminder-interval-input"
                        />
                        <p className="text-xs text-slate-400 mt-1">e.g., 2 = every other day after due</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600">Max overdue reminders</Label>
                        <Input
                          type="number"
                          min={0}
                          max={50}
                          value={automatedSettings.payment_reminder_max_overdue ?? 5}
                          onChange={(e) => {
                            const v = Math.max(0, Math.min(50, Number(e.target.value) || 0));
                            setAutomatedSettings(prev => ({ ...prev, payment_reminder_max_overdue: v }));
                          }}
                          onBlur={async () => {
                            try {
                              setSavingSettings(true);
                              await api.put('/email/automated-settings', automatedSettings);
                              toast.success('Cap updated');
                            } catch {
                              toast.error('Failed to save');
                            } finally {
                              setSavingSettings(false);
                            }
                          }}
                          className="h-9"
                          data-testid="payment-reminder-max-overdue-input"
                        />
                        <p className="text-xs text-slate-400 mt-1">Stops after this many overdue nudges</p>
                      </div>
                      <div className="flex items-end">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={async () => {
                            try {
                              setSavingSettings(true);
                              const { data } = await api.post('/email/auto-reminders/run-now');
                              if (data?.enabled === false) {
                                toast.warning('Auto-reminders are disabled — turn the toggle on first.');
                              } else {
                                toast.success(
                                  `Auto-reminders ran: ${data.sent} sent, ${data.skipped} skipped`
                                );
                              }
                            } catch (e) {
                              toast.error('Failed to run auto-reminders');
                            } finally {
                              setSavingSettings(false);
                            }
                          }}
                          disabled={savingSettings}
                          data-testid="payment-reminder-run-now-btn"
                        >
                          Run reminders now
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Birthday tools are available only with the Birthday Emails add-on. */}
            {isPlanFeatureEnabled('comms.birthday') && (
              <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Cake className="h-5 w-5 text-pink-500" />
                      Birthday Email Template
                    </CardTitle>
                    <CardDescription>
                      Customize the birthday wishes email sent to players - runs daily at 8:00 AM
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          setSendingBirthdayEmails(true);
                          const res = await api.post('/email/send-birthday-emails');
                          toast.success(`Birthday emails sent: ${res.data.sent} sent, ${res.data.skipped} skipped`);
                          fetchData();
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Failed to send birthday emails');
                        } finally {
                          setSendingBirthdayEmails(false);
                        }
                      }}
                      disabled={sendingBirthdayEmails || !automatedSettings.birthday_emails_enabled}
                    >
                      {sendingBirthdayEmails ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Play className="h-4 w-4 mr-2" />
                      )}
                      Send Today's Birthday Emails
                    </Button>
                    {birthdayTemplate?.is_active ? (
                      <Badge className="bg-green-100 text-green-700">Active</Badge>
                    ) : (
                      <Badge variant="outline" className="text-slate-500">Inactive</Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{birthdayTemplate?.name || 'Player Birthday Wishes'}</p>
                        <p className="text-sm text-slate-500 mt-1">Subject: {birthdayTemplate?.subject || 'Not configured'}</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          // Find the birthday template and open edit dialog
                          if (birthdayTemplate) {
                            setTemplateForm({
                              name: birthdayTemplate.name || '',
                              subject: birthdayTemplate.subject || '',
                              body: birthdayTemplate.body || '',
                              category: 'birthday',
                              is_active: birthdayTemplate.is_active !== false,
                              description: birthdayTemplate.description || ''
                            });
                            setSelectedTemplate(birthdayTemplate);
                            setShowTemplateDialog(true);
                          }
                        }}
                      >
                        <Edit2 className="h-4 w-4 mr-2" />
                        Edit Template
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Gift className="h-4 w-4" />
                    <span>Includes 20% discount code for in-store purchases</span>
                  </div>
                  <p className="text-xs text-slate-400">
                    Available placeholders: {`{{player_name}}, {{recipient_name}}, {{age}}, {{academy_name}}, {{discount_code}}, {{discount_percentage}}`}
                  </p>
                </div>
              </CardContent>
              </Card>
            )}

            {/* Birthday Email Log */}
            {isPlanFeatureEnabled('comms.birthday') && birthdayLogs.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recent Birthday Emails</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b">
                        <tr>
                          <th className="text-left p-4 font-medium text-slate-600">Sent At</th>
                          <th className="text-left p-4 font-medium text-slate-600">Player</th>
                          <th className="text-left p-4 font-medium text-slate-600">Recipients</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {birthdayLogs.slice(0, 20).map((log, idx) => (
                          <tr key={idx} className="hover:bg-slate-50">
                            <td className="p-4 text-sm">
                              {new Date(log.sent_at).toLocaleString()}
                            </td>
                            <td className="p-4 text-sm font-medium">{log.player_name}</td>
                            <td className="p-4 text-sm text-slate-600">
                              {log.recipients?.join(', ')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Logs Tab */}
          <TabsContent value="logs" className="mt-6">
            {emailLogs.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <Clock className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No email logs yet</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b">
                        <tr>
                          <th className="text-left p-4 font-medium text-slate-600">Date</th>
                          <th className="text-left p-4 font-medium text-slate-600">Template</th>
                          <th className="text-left p-4 font-medium text-slate-600">Type</th>
                          <th className="text-center p-4 font-medium text-slate-600">Recipients</th>
                          <th className="text-center p-4 font-medium text-slate-600">Sent</th>
                          <th className="text-center p-4 font-medium text-slate-600">Failed</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {emailLogs.map(log => (
                          <tr key={log.id} className="hover:bg-slate-50">
                            <td className="p-4 text-sm">
                              {new Date(log.sent_at).toLocaleString()}
                            </td>
                            <td className="p-4 text-sm font-medium">{log.template_name}</td>
                            <td className="p-4">
                              <Badge variant="outline">{log.type}</Badge>
                            </td>
                            <td className="p-4 text-center">{log.recipient_count}</td>
                            <td className="p-4 text-center">
                              <span className="text-green-600 font-medium">{log.sent_count}</span>
                            </td>
                            <td className="p-4 text-center">
                              <span className={log.failed_count > 0 ? 'text-red-600 font-medium' : 'text-slate-400'}>
                                {log.failed_count}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Delete Template Confirmation Dialog */}
      <AlertDialog open={deleteTemplateDialog} onOpenChange={setDeleteTemplateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-600" />
              Delete Template
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this email template? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTemplate}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create/Edit Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-500" />
              {selectedTemplate ? 'Edit Template' : 'Create Email Template'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Template Name *</Label>
                <Input
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  placeholder="e.g., Welcome Email"
                  data-testid="template-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={templateForm.category}
                  onValueChange={(v) => setTemplateForm({ ...templateForm, category: v })}
                >
                  <SelectTrigger data-testid="template-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {visibleCategories.filter(cat => cat.value !== 'birthday').map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Subject Line *</Label>
              <Input
                value={templateForm.subject}
                onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                placeholder="e.g., Welcome to Delhi Capitals Cricket Academy NJ!"
                data-testid="template-subject-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Description (internal use)</Label>
              <Input
                value={templateForm.description}
                onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                placeholder="Brief description of when to use this template"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Email Body (HTML) *</Label>
                <div className="flex flex-wrap gap-1">
                  {PLACEHOLDERS.slice(0, 4).map(p => (
                    <Button
                      key={p.key}
                      variant="outline"
                      size="sm"
                      className="text-xs h-6"
                      onClick={() => insertPlaceholder(p.key)}
                      title={p.description}
                    >
                      {p.key}
                    </Button>
                  ))}
                  <BillingPlaceholderPicker onInsert={insertPlaceholder} />
                </div>
              </div>
              <RichTextEditor
                value={templateForm.body}
                onChange={(value) => setTemplateForm({ ...templateForm, body: value })}
                placeholder="Dear {{parent_name}}, Welcome to Delhi Capitals Cricket Academy NJ..."
                minHeight="200px"
                maxHeight="400px"
                data-testid="template-body-input"
              />
              <p className="text-xs text-slate-500">
                Use placeholders like {'{{parent_name}}'}, {'{{player_name}}'} to personalize emails. Click placeholder buttons above to insert them.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleSaveTemplate} 
              disabled={saving}
              className="bg-blue-500 hover:bg-blue-600"
              data-testid="save-template-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {selectedTemplate ? 'Update Template' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Email Dialog */}
      <Dialog open={showBulkEmailDialog} onOpenChange={setShowBulkEmailDialog}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bulk-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-blue-500" />
              Send Bulk Email
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Step 1: Select Template */}
            <div className="space-y-2">
              <Label className="text-base font-medium">1. Select Template</Label>
              <Select
                value={selectedTemplate?.id || ''}
                onValueChange={(id) => {
                  const template = templates.find(t => t.id === id);
                  setSelectedTemplate(template);
                  // Reset form including recipient selection when template changes
                  setBulkEmailForm({
                    recipient_type: 'all',
                    program_ids: [],
                    selected_recipients: [],
                    enrollment_status_filter: 'active_or_paused',
                    custom_subject: template?.subject || '',
                    custom_body: template?.body || ''
                  });
                  setRecipientSearch('');
                  setSearchResults([]);
                  // Clear recipient preview immediately
                  setRecipientPreview({ total_count: 0, preview: [] });
                  // Fetch fresh preview for "all" recipients
                  fetchRecipientPreview('all', [], 'active_or_paused');
                }}
              >
                <SelectTrigger data-testid="bulk-template-select">
                  <SelectValue placeholder="Choose a template" />
                </SelectTrigger>
                <SelectContent>
                  {visibleTemplates.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} ({t.category})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Step 2: Select Recipients */}
            <div className="space-y-3">
              <Label className="text-base font-medium">2. Select Recipients</Label>
              <div className="flex gap-4 flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="recipient_type"
                    checked={bulkEmailForm.recipient_type === 'all'}
                    onChange={() => handleRecipientTypeChange('all')}
                    className="w-4 h-4"
                  />
                  <span>All Parents</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="recipient_type"
                    checked={bulkEmailForm.recipient_type === 'by_program'}
                    onChange={() => handleRecipientTypeChange('by_program')}
                    className="w-4 h-4"
                  />
                  <span>By Program</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="recipient_type"
                    checked={bulkEmailForm.recipient_type === 'individual'}
                    onChange={() => handleRecipientTypeChange('individual')}
                    className="w-4 h-4"
                  />
                  <span>Individual Recipients</span>
                </label>
              </div>

              {/* Enrollment Status Filter - only show for all/by_program */}
              {bulkEmailForm.recipient_type !== 'individual' && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <Label className="text-sm font-medium text-amber-800 mb-2 block">
                    Filter by Enrollment Status
                  </Label>
                  <div className="flex flex-wrap gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="enrollment_status"
                        checked={bulkEmailForm.enrollment_status_filter === 'active_or_paused'}
                        onChange={() => handleEnrollmentStatusFilterChange('active_or_paused')}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Active or Paused Enrollments</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="enrollment_status"
                        checked={bulkEmailForm.enrollment_status_filter === 'active_only'}
                        onChange={() => handleEnrollmentStatusFilterChange('active_only')}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Active Only</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="enrollment_status"
                        checked={bulkEmailForm.enrollment_status_filter === 'all'}
                        onChange={() => handleEnrollmentStatusFilterChange('all')}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">All Parents (no filter)</span>
                    </label>
                  </div>
                  <p className="text-xs text-amber-700 mt-2">
                    Select "Active or Paused" to only send to parents whose children have active or paused enrollments.
                  </p>
                </div>
              )}

              {bulkEmailForm.recipient_type === 'by_program' && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-3 bg-slate-50 rounded-lg">
                  {programs.map(program => (
                    <label key={program.id} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={bulkEmailForm.program_ids.includes(program.id)}
                        onCheckedChange={() => handleProgramSelect(program.id)}
                      />
                      <span className="text-sm">{program.name}</span>
                    </label>
                  ))}
                </div>
              )}

              {/* Individual Recipients Search */}
              {bulkEmailForm.recipient_type === 'individual' && (
                <div className="space-y-3">
                  <div className="relative">
                    <Input
                      placeholder="Search by name or email..."
                      value={recipientSearch}
                      onChange={(e) => {
                        setRecipientSearch(e.target.value);
                        searchRecipients(e.target.value);
                      }}
                      className="pr-10"
                      data-testid="recipient-search-input"
                    />
                    {searchingRecipients && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                      </div>
                    )}
                  </div>
                  
                  {/* Search Results */}
                  {searchResults.length > 0 && (
                    <div className="border rounded-lg max-h-48 overflow-y-auto">
                      {searchResults.map((result, idx) => (
                        <div 
                          key={idx}
                          className="p-2 hover:bg-slate-50 cursor-pointer flex items-center justify-between border-b last:border-b-0"
                          onClick={() => handleAddRecipient(result)}
                        >
                          <div>
                            <p className="font-medium text-sm">{result.name}</p>
                            <p className="text-xs text-slate-500">{result.email}</p>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {result.type === 'parent' ? 'Parent' : 'Player'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Selected Recipients */}
                  {bulkEmailForm.selected_recipients.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-slate-700">Selected Recipients:</p>
                      <div className="flex flex-wrap gap-2">
                        {bulkEmailForm.selected_recipients.map((recipient, idx) => (
                          <Badge 
                            key={idx}
                            variant="secondary"
                            className="flex items-center gap-1 pr-1"
                          >
                            {recipient.name}
                            <button
                              onClick={() => handleRemoveRecipient(recipient.email)}
                              className="ml-1 hover:bg-slate-300 rounded-full p-0.5"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Recipients Preview */}
              <div className="p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-4 w-4 text-blue-500" />
                  <span className="font-medium text-blue-700">
                    {bulkEmailForm.recipient_type === 'individual' 
                      ? `${bulkEmailForm.selected_recipients.length} recipients selected`
                      : `${recipientPreview.total_count} recipients selected`
                    }
                  </span>
                </div>
                {bulkEmailForm.recipient_type === 'individual' ? (
                  bulkEmailForm.selected_recipients.length > 0 && (
                    <p className="text-xs text-blue-600">
                      {bulkEmailForm.selected_recipients.map(r => r.email).join(', ')}
                    </p>
                  )
                ) : (
                  recipientPreview.preview.length > 0 && (
                    <p className="text-xs text-blue-600">
                      Preview: {recipientPreview.preview.map(p => p.email).join(', ')}
                      {recipientPreview.total_count > 10 && '...'}
                    </p>
                  )
                )}
              </div>
            </div>

            {/* Step 3: Customize (Optional) */}
            {selectedTemplate && (
              <div className="space-y-3">
                <Label className="text-base font-medium">3. Customize (Optional)</Label>
                <div className="space-y-2">
                  <Label>Subject</Label>
                  <Input
                    value={bulkEmailForm.custom_subject}
                    onChange={(e) => setBulkEmailForm({ ...bulkEmailForm, custom_subject: e.target.value })}
                    data-testid="bulk-subject-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Body</Label>
                  <RichTextEditor
                    value={bulkEmailForm.custom_body}
                    onChange={(value) => setBulkEmailForm({ ...bulkEmailForm, custom_body: value })}
                    minHeight="150px"
                    maxHeight="300px"
                    data-testid="bulk-body-input"
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkEmailDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleSendBulkEmail} 
              disabled={sending || !selectedTemplate || recipientPreview.total_count === 0}
              className="bg-blue-500 hover:bg-blue-600"
              data-testid="send-bulk-email-btn"
            >
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send to {recipientPreview.total_count} Recipients
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Template Preview: {previewTemplate?.name}</DialogTitle>
          </DialogHeader>
          {previewTemplate && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <strong>Note:</strong> Tags like {"{{parent_name}}"} will be replaced with actual values when the email is sent.
              </div>
              <div>
                <Label className="text-slate-500">Subject</Label>
                <p className="font-medium">{previewTemplate.subject}</p>
              </div>
              <div>
                <Label className="text-slate-500">Body</Label>
                <div 
                  className="mt-2 p-4 bg-white border rounded-lg prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(markdownToHtml(previewTemplate.body)) }}
                />
              </div>
              {/* Send Test Email Section */}
              <div className="border-t pt-4">
                <Label className="text-slate-500">Send Test Email</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    type="email"
                    placeholder="your-email@example.com"
                    value={testEmailAddress}
                    onChange={(e) => setTestEmailAddress(e.target.value)}
                    className="flex-1"
                  />
                  <Button 
                    variant="outline"
                    onClick={async () => {
                      if (!testEmailAddress) {
                        toast.error('Please enter an email address');
                        return;
                      }
                      try {
                        await api.post('/email/send-test', {
                          template_id: previewTemplate.id,
                          to_email: testEmailAddress
                        });
                        toast.success('Test email sent! Check your inbox.');
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to send test email');
                      }
                    }}
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Send Test
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Tags will be replaced with sample values in the test email.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>Close</Button>
            <Button onClick={() => {
              setShowPreviewDialog(false);
              openBulkEmailDialog(previewTemplate);
            }} className="bg-blue-500 hover:bg-blue-600">
              <Send className="h-4 w-4 mr-2" />
              Use This Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Management Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tags className="h-5 w-5" />
              Manage Template Types
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Create New Category */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Create New Type</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Display Name *</Label>
                    <Input
                      placeholder="e.g., Promotional"
                      value={categoryForm.name}
                      onChange={(e) => setCategoryForm({ 
                        ...categoryForm, 
                        name: e.target.value,
                        value: e.target.value.toLowerCase().replace(/\s+/g, '_')
                      })}
                      data-testid="category-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Value (auto-generated)</Label>
                    <Input
                      placeholder="promotional"
                      value={categoryForm.value}
                      onChange={(e) => setCategoryForm({ ...categoryForm, value: e.target.value })}
                      className="font-mono text-sm"
                      data-testid="category-value-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Color Theme</Label>
                  <Select 
                    value={categoryForm.color} 
                    onValueChange={(v) => setCategoryForm({ ...categoryForm, color: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bg-slate-100 text-slate-700">
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded bg-slate-200"></span> Gray
                        </div>
                      </SelectItem>
                      <SelectItem value="bg-blue-100 text-blue-700">
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded bg-blue-200"></span> Blue
                        </div>
                      </SelectItem>
                      <SelectItem value="bg-green-100 text-green-700">
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded bg-green-200"></span> Green
                        </div>
                      </SelectItem>
                      <SelectItem value="bg-amber-100 text-amber-700">
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded bg-amber-200"></span> Amber
                        </div>
                      </SelectItem>
                      <SelectItem value="bg-purple-100 text-purple-700">
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded bg-purple-200"></span> Purple
                        </div>
                      </SelectItem>
                      <SelectItem value="bg-pink-100 text-pink-700">
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded bg-pink-200"></span> Pink
                        </div>
                      </SelectItem>
                      <SelectItem value="bg-orange-100 text-orange-700">
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded bg-orange-200"></span> Orange
                        </div>
                      </SelectItem>
                      <SelectItem value="bg-cyan-100 text-cyan-700">
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded bg-cyan-200"></span> Cyan
                        </div>
                      </SelectItem>
                      <SelectItem value="bg-teal-100 text-teal-700">
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded bg-teal-200"></span> Teal
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Description (optional)</Label>
                  <Input
                    placeholder="What type of emails use this category?"
                    value={categoryForm.description}
                    onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                  />
                </div>
                
                <Button 
                  onClick={handleCreateCategory} 
                  disabled={savingCategory || !categoryForm.name}
                  data-testid="create-category-btn"
                >
                  {savingCategory ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  Create Type
                </Button>
              </CardContent>
            </Card>
            
            {/* Existing Categories */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">All Template Types</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {visibleCategories.map(cat => (
                    <div 
                      key={cat.value}
                      className="flex items-center justify-between p-3 rounded-lg border bg-white"
                    >
                      <div className="flex items-center gap-3">
                        <Badge className={cat.color}>{cat.name}</Badge>
                        <span className="text-sm text-slate-500 font-mono">{cat.value}</span>
                        {cat.is_system && (
                          <Badge variant="outline" className="text-xs">System</Badge>
                        )}
                      </div>
                      {!cat.is_system && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteCategory(cat.value)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default EmailTemplates;
